/* eslint-disable no-unused-vars */
/* eslint-disable camelcase */
import React, {
  useState,
  useEffect,
  useRef,
  Fragment,
  useCallback
} from 'react'
import { Container, Modal, Stack } from '@mui/material'
import { useHistory } from 'react-router-dom'
import { store } from '../store'
import { loadingActions } from '../store/loading'
import {
  DndContext,
  closestCenter,
  pointerWithin,
  rectIntersection,
  useSensor,
  MouseSensor,
  useSensors,
  TouchSensor,
  getFirstCollision
} from '@dnd-kit/core'
import { useTranslation } from 'react-i18next'
import { arrayMove } from '@dnd-kit/sortable'
import { useSelector } from 'react-redux'
import { sortBy } from '../utils/global'
import { statusOptions } from '../utils/fieldForms'
import * as ApiServices from '../services/ApiService'
import TopBar from '../components/formBuilder/TopBar'
import ConfirmationModal from '../components/general/ConfirmationModal'
import RightPanel from '../components/formBuilder/RightPanel'

import LeftPanel from '../components/formBuilder/LeftPanel'
import MiddlePanel from '../components/formBuilder/MiddlePanel'
import {
  customElements,
  elements,
  getFirstKeyFromObject,
  cloneDeep,
  elementKeys,
  checkNotFullfilled,
  auditOptions,
  auditType,
  elementTypes
} from '../utils/formBuilder'
import { formBuilderMainStyles } from '../styles/classes/FormBuilderClasses'
import moment from 'moment'
import { Ring } from 'react-awesome-spinners'
import FormPreview from '../components/formBuilder/FormPreview'
import customTheme from '../styles/mui_theme'
const defaultObject = {
  name: 'Default'
}

const FormBuilder = () => {
  const { classes } = formBuilderMainStyles()
  const { t } = useTranslation()
  const history = useHistory()
  // Data from store
  const currentUser = useSelector(state => state.auth.user)
  const tradesFromStore = useSelector(state => state.data.trades)
  const woTypesFromStore = useSelector(state => state.data.workOrderTypes)
  const currentScreenConfig = useSelector(
    state => state.data.currentScreenConfig
  )
  const screenConfigList = useSelector(state => state.data.screenConfigsList)
  const [loading, setLoading] = useState(false)
  // edit handlers
  const [changes, setChanges] = useState(false)
  const [showConfirmationModal, setShowConfirmationModal] = useState(false)
  const [elementMissingFields, setElementsMissingFields] = useState({})
  // modal states
  const [showPreview, setShowPreview] = useState(false)
  const [modalMode, setModalMode] = useState(null)
  const [extraMessage, setExtraMessage] = useState('')
  const [mainButtonStyle, setMainButtonStyle] = useState(null)
  const [extraConditions, setExtraConditions] = useState(false)
  const [extraMessageStyle, setExtraMessageStyle] = useState(null)
  const [mainActionLabel, setMainActionLabel] = useState('')
  const [secondaryActionLabel, setSecondaryActionLabel] = useState('')
  const [title, setTitle] = useState(null)
  const [message, setMessage] = useState(null)
  const [hideSecondaryAction, setHideSecondaryAction] = useState(false)
  const [selectedElement, setSelectedElement] = useState(null) // also selected item
  const [selectedObjectCounter, setSelectedObjectCounter] = useState({})
  const [mandatoryFields, setMandatoryFields] = useState([
    elementKeys.TITLE,
    elementKeys.OPTIONS,
    elementKeys.PHOTOS,
    elementKeys.BUTTONS,
    elementKeys.ATTACHMENTS
  ])
  const [deleteElementId, setDeleteElementId] = useState(null)
  const [recentlyAddedItemId, setRecentlyAddedItemId] = useState(null)
  const bottomRef = useRef(null)
  // edit configurations
  // TODO managing multiple pages
  const [pageIndex, setPageIndex] = useState(0)
  const [fieldConfigurations, setFieldConfigurations] = useState(
    currentScreenConfig?.configurations
  )
  const sensors = useSensors(
    useSensor(MouseSensor, {
      // Require the mouse to move by 10 pixels before activating
      activationConstraint: {
        distance: 10
      }
    }),
    useSensor(TouchSensor)
  )

  // Drag and drop functionality
  // sort
  const sortedElements = elements?.sort((a, b) => sortBy(a, b, 'type'))
  // add keys/ids to elements based on unix time
  const processedElements = [...sortedElements, ...customElements].map(
    (element, idx) => {
      if (element.type === elementTypes.MATERIAL_PARTS) {
        element.title = t('form_builder.elements.material_parts')
        if (extraConditions.materialPartsTotalAmountDue) {
          element.total_amount_due = false
        }
      }
      if (element.type === elementTypes.AUTHORIZATION_FIELD) {
        element.title = t('form_builder.elements.authorization_field_title')
      }
      const id = Math.round(Math.random() * Date.now()).toString(16)
      return {
        [id]: { ...element }
      }
    }
  )
  const [draggableComponents, setDraggableComponents] =
    useState(processedElements)

  const selectItemHandler = element => {
    if (!element) return
    const id = getFirstKeyFromObject(element)
    const selectedTemp = cloneDeep(selectedObjectCounter)
    if (selectedTemp[id]) {
      selectedTemp[id]++
    } else {
      selectedTemp[id] = 1
    }
    setSelectedObjectCounter(selectedTemp)
    setSelectedElement(element)
  }

  // nested logic Functions
  const nestedLogicTrueHandler = (
    elementType,
    questionId,
    logicalActionValue
  ) => {
    if (!changes) setChanges(true)
    setSectionsList(prevSectionsList => {
      const modSectionsList = cloneDeep(prevSectionsList)
      const sectionKey = findContainer(questionId)
      const sectionIdx = prevSectionsList.findIndex(
        section => getFirstKeyFromObject(section) === sectionKey
      )

      // element to add start
      const foundDraggableIndex = draggableComponents.findIndex(
        item => item[getFirstKeyFromObject(item)].type === elementType
      )
      const itemToAdd = cloneDeep(draggableComponents[foundDraggableIndex])
      const itemToAddKey = getFirstKeyFromObject(itemToAdd)
      let itemToAddObj = itemToAdd[itemToAddKey]
      itemToAddObj = {
        ...itemToAddObj,
        dependency: {
          question: `${sectionKey}.${questionId}`,
          value: logicalActionValue
        }
      }
      // element to add end
      const questions = modSectionsList[sectionIdx][sectionKey].questions
      const questionIndex = questions.findIndex(
        question => getFirstKeyFromObject(question) === questionId
      )
      modSectionsList[sectionIdx][sectionKey].questions = modSectionsList[
        sectionIdx
      ][sectionKey].questions.filter(item => {
        const itemKey = getFirstKeyFromObject(item)
        const itemData = item[itemKey]
        return itemData?.dependency?.question !== `${sectionKey}.${questionId}`
      })
      modSectionsList[sectionIdx][sectionKey].questions.splice(
        questionIndex + 1,
        0,
        { [itemToAddKey]: itemToAddObj }
      )
      replaceDraggedComponent(foundDraggableIndex)
      return modSectionsList
    })
  }

  const nestedLogicFalseHandler = questionId => {
    if (!changes) setChanges(true)
    setSectionsList(prevSectionsList => {
      const modSectionsList = cloneDeep(prevSectionsList)
      const sectionKey = findContainer(questionId)
      const sectionIdx = prevSectionsList.findIndex(
        section => getFirstKeyFromObject(section) === sectionKey
      )
      modSectionsList[sectionIdx][sectionKey].questions = modSectionsList[
        sectionIdx
      ][sectionKey].questions.filter(item => {
        const itemKey = getFirstKeyFromObject(item)
        const itemData = item[itemKey]
        return itemData?.dependency?.question !== `${sectionKey}.${questionId}`
      })
      return modSectionsList
    })
  }
  const updateNestedLogicItemHandler = (questionId, logicalActionValue) => {
    if (!changes) setChanges(true)
    setSectionsList(prevSectionsList => {
      const modSectionsList = cloneDeep(prevSectionsList)
      const sectionKey = findContainer(questionId)
      const sectionIdx = prevSectionsList.findIndex(
        section => getFirstKeyFromObject(section) === sectionKey
      )

      const questions = modSectionsList[sectionIdx][sectionKey].questions
      const nestedQuestionIndex = questions.findIndex(item => {
        const itemKey = getFirstKeyFromObject(item)
        const itemData = item[itemKey]
        return itemData?.dependency?.question === `${sectionKey}.${questionId}`
      })
      if (nestedQuestionIndex !== -1) {
        const nestedQuestionKey = getFirstKeyFromObject(
          modSectionsList[sectionIdx][sectionKey].questions[nestedQuestionIndex]
        )
        modSectionsList[sectionIdx][sectionKey].questions[nestedQuestionIndex][
          nestedQuestionKey
        ].dependency.value = logicalActionValue
      }
      return modSectionsList
    })
  }

  function changeTotalAmountDueToFalse (element) {
    const id = getFirstKeyFromObject(element)
    const obj = element[id]
    if (
      obj.type === elementTypes.LINE_ITEMS &&
      extraConditions.lineItemsTotalAmountDue
    ) {
      obj.total_amount_due = false
    }
    if (
      obj.type === elementTypes.MATERIAL_PARTS &&
      extraConditions.materialPartsTotalAmountDue
    ) {
      obj.total_amount_due = false
    }
  }

  // action for clicking the + in the element
  const clickAddHandler = element => {
    if (!changes) setChanges(true)
    const foundIndex = draggableComponents.findIndex(
      item =>
        item[getFirstKeyFromObject(item)] ===
        element[getFirstKeyFromObject(element)]
    )

    setSectionsList(prevSectionsList => {
      const modSectionsList = cloneDeep(prevSectionsList)
      const sectionIndex = modSectionsList.length - 1
      const sectionKey = getFirstKeyFromObject(modSectionsList[sectionIndex])
      const draggableCmp = draggableComponents[foundIndex]
      changeTotalAmountDueToFalse(draggableCmp)
      modSectionsList[sectionIndex][sectionKey].questions = [
        ...modSectionsList[sectionIndex][sectionKey].questions,
        draggableCmp
      ]
      return modSectionsList
    })
    setRecentlyAddedItemId(
      getFirstKeyFromObject(draggableComponents[foundIndex])
    )
    replaceDraggedComponent(foundIndex)
    // scroll all the way to the bottom after adding
    bottomRef.current?.scrollIntoView({
      behavior: 'smooth',
      block: 'end',
      inline: 'end'
    })
    setRecentlyAddedItemId(null)
    // after scrolling wait 5 seconds and
    setTimeout(() => {
      setRecentlyAddedItemId(null)
    }, 2000)
  }

  // we need to replace drag and drop object
  // after beind dropped in the middle panel
  const replaceDraggedComponent = indexInDraggableComponents => {
    // find active component in draggable list
    const tmp = [...draggableComponents]
    const unixTime = Math.round(Math.random() * Date.now()).toString(16)
    const coreObject =
      tmp[indexInDraggableComponents][
        getFirstKeyFromObject(tmp[indexInDraggableComponents])
      ]
    tmp[indexInDraggableComponents] = { [unixTime]: { ...coreObject } }
    setDraggableComponents([...tmp])
  }

  // edit section name
  const editSectionNameHandler = (sectionNameValue, pageIdx, sectionIdx) => {
    if (!changes) setChanges(true)
    setFieldConfigurations(prevValues => {
      const editablePrevValues = cloneDeep(prevValues)
      const sectionKey = getFirstKeyFromObject(
        editablePrevValues[pageIdx].page_questions[sectionIdx]
      )
      editablePrevValues[pageIdx].page_questions[sectionIdx][sectionKey].title =
        sectionNameValue
      setSectionsList(editablePrevValues[pageIdx].page_questions)
      return editablePrevValues
    })
  }

  const editSectionHandlerMiddlePanel = (sectionNameValue, sectionIndex) => {
    if (!changes) setChanges(true)
    setSectionsList(prevSectionsList => {
      const modSectionsList = cloneDeep(prevSectionsList)
      const sectionKey = getFirstKeyFromObject(modSectionsList[sectionIndex])
      modSectionsList[sectionIndex][sectionKey].title = sectionNameValue
      return modSectionsList
    })
  }
  // remove section
  const removeSectionHandler = (pageIdx, sectionIdx) => {
    if (!changes) setChanges(true)
    setFieldConfigurations(prevValues => {
      const editablePrevValues = cloneDeep(prevValues)
      const sections = editablePrevValues[pageIdx].page_questions
      const sectionId = getFirstKeyFromObject(sections[sectionIdx])
      const Filtered = sections.filter(
        section => getFirstKeyFromObject(section) !== sectionId
      )
      editablePrevValues[pageIdx].page_questions = Filtered
      setSectionsList(editablePrevValues[pageIndex].page_questions)
      return editablePrevValues
    })
  }

  // each time there is a change in selected component (active item)
  // reflect it in dropped component
  useEffect(() => {
    // should we use debouncing?
    if (selectedElement) {
      updateSectionsList()
    }
  }, [selectedElement])

  const updateSectionsList = () => {
    // check if already dropped
    // only do this if active item is already dropped
    if (!changes) setChanges(true)
    setSectionsList(prevSectionsList => {
      const modSectionsList = cloneDeep(prevSectionsList)
      const sectionId = findContainer(getFirstKeyFromObject(selectedElement))
      const sectionIdx = prevSectionsList?.findIndex(
        section => getFirstKeyFromObject(section) === sectionId
      )
      // question from active containerId section
      const questions = getSectionQuestions(prevSectionsList, sectionId)
      const questionId = getFirstKeyFromObject(selectedElement)
      const questionIndex = questions?.findIndex(
        item => questionId === getFirstKeyFromObject(item)
      )
      modSectionsList[sectionIdx][sectionId]?.questions?.splice(
        questionIndex,
        1,
        selectedElement
      )
      return modSectionsList
    })
  }

  const removeElementHandler = () => {
    if (deleteElementId) {
      if (!changes) setChanges(true)
      setSectionsList(prevSectionsList => {
        const modSectionsList = cloneDeep(prevSectionsList)
        const sectionId = findContainer(deleteElementId)
        const sectionIdx = prevSectionsList.findIndex(
          section => getFirstKeyFromObject(section) === sectionId
        )
        // question from active containerId section
        modSectionsList[sectionIdx][sectionId].questions = modSectionsList[
          sectionIdx
        ][sectionId].questions.filter(
          item => getFirstKeyFromObject(item) !== deleteElementId
        )
        setDeleteElementId(null)
        setSelectedElement(null)
        return modSectionsList
      })
    }
  }

  function checkElementsConditions (sections) {
    let hasAuthFieldElement = false
    let lineItemsCounter = 0
    let materialPartsCounter = 0
    // Iterate over each section in the sections array
    for (const section of sections) {
      // Iterate over each section key (like "section_8081c18805") and its content
      for (const sectionKey in section) {
        const questions = section[sectionKey].questions

        // Iterate over each question in the section
        for (const question of questions) {
          const questionDetails = question[getFirstKeyFromObject(question)]
          if (questionDetails?.type === elementTypes.AUTHORIZATION_FIELD) {
            hasAuthFieldElement = true
          }
          if (
            (questionDetails?.type === elementTypes.LINE_ITEMS ||
              questionDetails?.type === elementTypes.MATERIAL_PARTS) &&
            questionDetails?.total_amount_due === true
          ) {
            if (questionDetails?.type === elementTypes.LINE_ITEMS) {
              lineItemsCounter++
            }
            if (questionDetails?.type === elementTypes.MATERIAL_PARTS) {
              materialPartsCounter++
            }
          }
        }
      }
    }
    // Return false if no authorization field was found
    return {
      authorzationElement: hasAuthFieldElement,
      lineItemsTotalAmountDue: lineItemsCounter >= 1,
      materialPartsTotalAmountDue: materialPartsCounter >= 1
    }
  }

  // deleted an element from the middle panel
  const handleRemoveElementAction = id => {
    // TODO: check if item has data
    // if not just remove
    setDeleteElementId(id)
    setModalMode('delete_element')
    setTitle(t('form_builder.delete_element'))
    setMessage(t('form_builder.delete_element_message'))
    setMainActionLabel(t('form_builder.yes'))
    setSecondaryActionLabel(t('form_builder.cancel'))
    setShowConfirmationModal(true)
  }

  // clones an element from the middle panel
  const handleCloneElement = () => {
    if (selectedElement) {
      const newId = Math.round(Math.random() * Date.now()).toString(16)
      const newCloned = {
        [newId]: {
          ...selectedElement[getFirstKeyFromObject(selectedElement)]
        }
      }
      if (!changes) setChanges(true)
      setSectionsList(prevSectionsList => {
        const modSectionsList = cloneDeep(prevSectionsList)
        const sectionId = findContainer(getFirstKeyFromObject(selectedElement))
        const sectionIdx = prevSectionsList.findIndex(
          section => getFirstKeyFromObject(section) === sectionId
        )
        // question from active containerId section
        modSectionsList[sectionIdx][sectionId].questions = [
          ...modSectionsList[sectionIdx][sectionId].questions,
          newCloned
        ]
        return modSectionsList
      })

      setRecentlyAddedItemId(newId)
      // scroll all the way to the bottom after adding
      bottomRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'end',
        inline: 'end'
      })
      setTimeout(() => {
        setRecentlyAddedItemId(null)
      }, 2000)
    }
  }

  let initTradelist = [{ ...defaultObject }]
  if (tradesFromStore) {
    initTradelist = [{ ...defaultObject }, ...tradesFromStore]
  }

  let initTypesList = [{ ...defaultObject }]
  if (woTypesFromStore) {
    initTypesList = [{ ...defaultObject }, ...woTypesFromStore]
  }

  const tradeObj = initTradelist.find(
    trade => trade.name === currentScreenConfig?.trade
  )
  let initServicesList = [{ ...defaultObject }]
  if (
    tradeObj &&
    tradeObj?.trade_services &&
    tradeObj?.trade_services?.length > 0
  ) {
    const tradeObj = initTradelist.find(
      trade => trade.name === currentScreenConfig?.trade
    )
    const tradeServices = [...tradeObj?.trade_services]
    tradeServices.sort((a, b) => sortBy(a, b, 'name'))
    if (tradeServices?.length > 0) {
      initServicesList = [{ ...defaultObject }, ...tradeServices]
    }
  }

  // Details
  const [tradeList, setTradeList] = useState(initTradelist)
  const [serviceList, setServiceList] = useState(initServicesList)
  const [typeList, setTypeList] = useState(initTypesList)

  const [formName, setFormName] = useState(currentScreenConfig?.form_name)
  const [formDescription, setFormDescription] = useState(
    currentScreenConfig?.form_description
  )
  const [woType, setWoType] = useState({ name: currentScreenConfig?.work_type })
  const [service, setService] = useState({ name: currentScreenConfig?.service })
  const [trade, setTrade] = useState({ name: currentScreenConfig?.trade })
  const [status, setStatus] = useState(currentScreenConfig?.status)
  const missingData = checkMissingData()

  function checkMissingData () {
    const tradeName = trade?.name
    const serviceName = service?.name
    const typeName = woType?.name
    if (!tradeName || !serviceName || !typeName || !formDescription) {
      return true
    }
    return false
  }

  // edit functions
  const toggleChangeHandler = (event, newState) => {
    if (!changes) setChanges(true)
    setStatus(newState)
  }

  const woTypeChangeHandler = event => {
    if (!changes) setChanges(true)
    setWoType(event.target.value)
  }

  const tradeChangeHandler = event => {
    if (!changes) setChanges(true)
    setTrade(event.target.value)
    setService(null)
    if (event?.target?.value?.trade_services) {
      // copy array to avoid error when sorting
      const tradeServices = [...event?.target?.value?.trade_services]
      tradeServices?.sort((a, b) => sortBy(a, b, 'name'))
      setServiceList([defaultObject, ...tradeServices])
    }
  }

  const serviceChangeHandler = event => {
    if (!changes) setChanges(true)
    setService(event.target.value)
  }

  const formNameChangeHandler = event => {
    if (!changes) setChanges(true)
    setFormName(event.target.value)
  }

  const formDescriptionHandler = event => {
    if (!changes) setChanges(true)
    setFormDescription(event.target.value)
  }

  const getLastModifiedObject = () => {
    const unixTime = moment().unix()
    return {
      user_id: currentUser?.userId,
      date: unixTime,
      firstName: currentUser?.userInfo?.firstName,
      lastName: currentUser?.userInfo?.lastName
    }
  }

  const saveHandler = async () => {
    store.dispatch(loadingActions.refreshAuditTrail(true))
    // update form
    const tradeName = trade?.name
    const serviceName = service?.name
    const workTypeName = woType?.name
    const objectToUpdate = {
      form_name: formName,
      form_description: formDescription,
      status: status.toLowerCase(),
      last_modified: getLastModifiedObject(),
      config_id: currentUser?.userInfo?.config_id,
      configurations: fieldConfigurations,
      trade: tradeName,
      service: serviceName,
      work_type: workTypeName
    }
    await ApiServices.updateScreenConfiguration(
      currentScreenConfig.id,
      objectToUpdate
    )
    // create audit
    await createNewAudit(auditOptions.SAVED)
    setChanges(false)
  }

  const createNewAudit = async auditOption => {
    const unixTime = moment().unix()
    const auditToCreate = {
      user_id: currentUser.userId,
      date: unixTime,
      type: auditType,
      change: {
        status: auditOption
      },
      first_name: currentUser?.userInfo?.firstName,
      last_name: currentUser?.userInfo?.lastName,
      record_id: currentScreenConfig.id
    }
    await ApiServices.createAudit(auditToCreate)
  }

  const publishFormHandler = async () => {
    const objectToUpdate = {
      status: statusOptions.PUBLISHED,
      last_modified: getLastModifiedObject()
    }
    // update screen config to published
    await ApiServices.updateScreenConfiguration(
      currentScreenConfig.id,
      objectToUpdate
    )
    // create audit for update
    await createNewAudit(auditOptions.PUBLISHED)
    setChanges(false)
  }

  const onDeactivate = async () => {
    // clone deactivate form
    // use draft when cloning
    const objectToCreate = {
      ...currentScreenConfig,
      last_modified: getLastModifiedObject(),
      status: statusOptions.DRAFT
    }
    delete objectToCreate.id
    const newScreenConfiguration = await ApiServices.createScreenConfiguration(
      objectToCreate
    )
    // change current form status to deleted
    const objectToUpdate = {
      status: statusOptions.DELETED,
      last_modified: getLastModifiedObject()
    }
    await ApiServices.updateScreenConfiguration(
      currentScreenConfig.id,
      objectToUpdate
    )
    // migrate audit trail to new
    const migrationData = {
      old_record_id: currentScreenConfig.id,
      new_record_id: newScreenConfiguration.id,
      type: auditType
    }
    await createNewAudit(auditOptions.DEACTIVATED)
    await ApiServices.migrateAudits(migrationData)
    setChanges(false)
  }

  // functionality UI
  const backHandler = () => {
    if (changes) {
      setModalMode('unsaved_changes')
      setTitle(t('form_builder.unsaved_changes'))
      setMessage(t('form_builder.unsaved_changes_message'))
      setMainActionLabel(t('form_builder.save'))
      setSecondaryActionLabel(t('form_builder.discard'))
      setHideSecondaryAction(false)
      setShowConfirmationModal(true)
    } else {
      history.push('/field-forms')
    }
  }

  const mainActionModalHandler = async () => {
    switch (modalMode) {
      case 'publish':
        await publishFormHandler()
        history.push('/field-forms')
        break
      case 'delete_element':
        removeElementHandler()
        setShowConfirmationModal(false)
        break
      case 'unsaved_changes':
        await saveHandler()
        history.push('/field-forms')
        break
      case 'deactivate':
        await onDeactivate()
        history.push('/field-forms')
        break
      default:
        setShowConfirmationModal(false)
        break
    }
  }

  const secondaryActionModalHandler = async () => {
    switch (modalMode) {
      case 'unsaved_changes':
        setShowConfirmationModal(false)
        history.push('/field-forms')
        break
      case 'duplicate_publish':
        setShowConfirmationModal(false)
        break
      default:
        setShowConfirmationModal(false)
        break
    }
  }

  const showPreviewHandler = () => {
    setShowPreview(prevValue => !prevValue)
  }

  const checkMissingMandatoryFields = () => {
    const questionsMissingFields = {}
    const selectedTemp = cloneDeep(selectedObjectCounter)
    // check missing values in the middle
    const configurations = cloneDeep(fieldConfigurations)
    // for each page
    for (const page of configurations) {
      const sections = page?.page_questions ?? []
      // for each section
      for (const section of sections) {
        const sectionId = getFirstKeyFromObject(section)
        const questions = section[sectionId].questions
        // for each question
        for (const question of questions) {
          const questionId = getFirstKeyFromObject(question)
          const questionObj = question[questionId]
          // simulate that I selected every item at least 2 times
          // for showing errors in
          if (selectedTemp[questionId]) {
            selectedTemp[questionId]++
          } else {
            selectedTemp[questionId] = 2
          }
          // for each mandatory field
          for (const field of mandatoryFields) {
            // check if question has key field
            if (Object.prototype.hasOwnProperty.call(questionObj, field)) {
              const notFullfilled = checkNotFullfilled(questionObj, field)
              if (notFullfilled) {
                if (questionsMissingFields[questionId]) {
                  questionsMissingFields[questionId] = [
                    ...questionsMissingFields[questionId],
                    field
                  ]
                } else {
                  questionsMissingFields[questionId] = [field]
                }
              }
            }
          }
        }
      }
    }
    setSelectedObjectCounter(selectedTemp)
    // this is not used when editing an element
    // as it's a big process
    setElementsMissingFields(questionsMissingFields)
    return questionsMissingFields
  }

  const showDuplicateFormNameModal = formName => {
    setModalMode('duplicate_publish')
    setTitle(t('form_builder.duplicate_wo_title'))
    setMessage(
      <p>
        {t('form_builder.duplicate_form_name_message_0')}
        <span className={classes.boldText}>{formName}</span>
        {t('form_builder.duplicate_form_name_message_1')}
      </p>
    )
    setMainActionLabel(t('form_builder.ok'))
    setHideSecondaryAction(true)
    setShowConfirmationModal(true)
  }

  const showDuplicateTradeServiceAndWorkTypeModal = (
    tradeName,
    typeName,
    serviceName
  ) => {
    setModalMode('duplicate_publish')
    setTitle(t('form_builder.duplicate_wo_title'))
    setMessage(
      <p>
        {t('form_builder.duplicate_wo_message_0')}
        <span className={classes.boldText}>{tradeName}</span>
        {t('form_builder.duplicate_wo_message_1')}
        <span className={classes.boldText}>{typeName}</span>
        {t('form_builder.duplicate_wo_message_2')}
        <span className={classes.boldText}>{serviceName}</span>
        {t('form_builder.duplicate_wo_message_3')}
      </p>
    )
    setMainActionLabel(t('form_builder.ok'))
    setHideSecondaryAction(true)
    setShowConfirmationModal(true)
  }

  const showPublishModal = (tradeName, typeName, serviceName) => {
    setModalMode('publish')
    setTitle(t('form_builder.publish_form'))
    setMessage(
      <p>
        {t('form_builder.publish_form_message_0')}
        <span className={classes.boldText}>{` ${t(
          'form_builder.trade'
        )} - ${tradeName},`}</span>
        <span className={classes.boldText}>{`${' '} ${t(
          'form_builder.type'
        )} - ${typeName} ${' '}`}</span>
        {t('form_builder.publish_form_message_1')}
        <span className={classes.boldText}>{` ${t(
          'form_builder.service'
        )} - ${serviceName} ${' '}`}</span>
        {t('form_builder.publish_form_message_2')}
      </p>
    )
    setMainActionLabel(t('form_builder.publish'))
    setSecondaryActionLabel(t('form_builder.cancel'))
    setShowConfirmationModal(true)
  }

  const saveButtonHandler = async () => {
    const missingObj = checkMissingMandatoryFields()
    if (Object.keys(missingObj).length === 0) {
      await saveHandler()
      store.dispatch(loadingActions.refreshAuditTrail(false))
    }
  }

  const deactivateButtonHandler = () => {
    const tradeName = trade?.name
    const serviceName = service?.name
    const typeName = woType?.name
    setModalMode('deactivate')
    setTitle(t('form_builder.deactivate_field_form_title'))
    setMessage(
      <p>
        {t('form_builder.deactivate_form_main_message_0')}
        <br /> <br />
        {t('form_builder.deactivate_form_main_message_1')}
      </p>
    )
    setExtraMessage(t('form_builder.deactivate_form_extra_message'))
    setMainActionLabel(t('form_builder.center_panel.deactivate'))
    setSecondaryActionLabel(t('form_builder.cancel'))
    setMainButtonStyle(classes.destructiveButton)
    setExtraMessageStyle(classes.boldText)
    setShowConfirmationModal(true)
  }

  const publishButtonHandler = async () => {
    const duplicateFormName = screenConfigList.find(config => {
      return (
        config.form_name === formName &&
        config.status === statusOptions.PUBLISHED
      )
    })

    if (duplicateFormName) {
      showDuplicateFormNameModal(formName)
      return
    }

    const tradeName = trade?.name
    const serviceName = service?.name
    const typeName = woType?.name
    const duplicateTradeServiceAndWorkType = screenConfigList.find(config => {
      return (
        config.trade === tradeName &&
        config.service === serviceName &&
        config.work_type === typeName &&
        config.status === statusOptions.PUBLISHED
      )
    })

    if (duplicateTradeServiceAndWorkType) {
      showDuplicateTradeServiceAndWorkTypeModal(
        tradeName,
        typeName,
        serviceName
      )
      return
    }
    showPublishModal(tradeName, typeName, serviceName)
  }
  // TODO: change color of the ring
  if (loading) {
    return (
      <Container className={classes.loaderContainer}>
        <Ring className={classes.ring} />
      </Container>
    )
  }

  const createNewSectionName = sections => {
    const currentTitles = sections.map(
      section => section[getFirstKeyFromObject(section)].title
    )
    let sectionNumber = 1
    let sectionTitle = `Section ${sectionNumber}`
    while (
      currentTitles.includes(sectionTitle) ||
      sectionNumber <= sections.length
    ) {
      sectionNumber++
      sectionTitle = `Section ${sectionNumber}`
    }
    return sectionTitle
  }

  // sections actions
  const addSectionHandler = () => {
    if (!changes) setChanges(true)
    setFieldConfigurations(prevValue => {
      const editablePrevValue = cloneDeep(prevValue)
      const sectionNumberId = Math.round(Math.random() * Date.now()).toString(
        16
      )
      const sectionKey = `section_${sectionNumberId}`
      const currentSections = editablePrevValue[pageIndex]?.page_questions
      const sectionTitle = createNewSectionName(currentSections)
      const newSectionObject = {
        [sectionKey]: { title: sectionTitle, questions: [] }
      }
      editablePrevValue[pageIndex].page_questions = [
        ...currentSections,
        { ...newSectionObject }
      ]
      setSectionsList(editablePrevValue[pageIndex].page_questions)
      return editablePrevValue
    })
  }

  const lastOverId = useState()
  const [activeId, setActiveId] = useState()
  const [clonedSectionsList, setClonedSectionsList] = useState()
  const [sectionsList, setSectionsList] = useState(
    fieldConfigurations[pageIndex]?.page_questions
  )
  const [emptySections, setEmptySections] = useState(true)
  const recentlyMovedToNewSection = useRef(false)
  const TRASH_ID = 'void'

  const checkIdInQuestionsList = (questionsList, questionId) => {
    const foundQuestion = questionsList.find(
      question => questionId === getFirstKeyFromObject(question)
    )
    if (foundQuestion) return true
    else return false
  }

  const dragCancelHandler = () => {
    if (clonedSectionsList) {
      // Reset items to their original state in case items have been
      // Dragged across containers
      setSectionsList(clonedSectionsList)
    }

    setActiveId(null)
    setClonedSectionsList(null)
  }

  /**
   * Custom collision detection strategy optimized for multiple containers
   *
   * - First, find any droppable containers intersecting with the pointer.
   * - If there are none, find intersecting containers with the active draggable.
   * - If there are no intersecting containers, return the last matched intersection
   *
   */
  const collisionDetectionStrategy = useCallback(
    args => {
      if (activeId && checkIdInSectionsList(activeId)) {
        return closestCenter({
          ...args,
          droppableContainers: args.droppableContainers.filter(container =>
            checkIdInSectionsList(container.id)
          )
        })
      }

      // Start by finding any intersecting droppable
      const pointerIntersections = pointerWithin(args)
      const intersections =
        pointerIntersections.length > 0
          ? pointerIntersections
          : rectIntersection(args)
      let overId = getFirstCollision(intersections, 'id')

      if (overId != null) {
        if (overId === TRASH_ID) {
          // If the intersecting droppable is the trash, return early
          // Remove this if you're not using trashable functionality in your app
          return intersections
        }

        if (checkIdInSectionsList(overId)) {
          const sectionIndex = sectionsList.findIndex(
            section => overId === getFirstKeyFromObject(section)
          )
          const sectionKey = getFirstKeyFromObject(sectionsList[sectionIndex])
          const questions = sectionsList[sectionIndex][sectionKey].questions

          // If a container is matched and it contains items (columns 'A', 'B', 'C')
          if (questions.length > 0) {
            // Return the closest droppable within that container
            overId = closestCenter({
              ...args,
              droppableContainers: args.droppableContainers.filter(
                container =>
                  container.id !== overId &&
                  checkIdInQuestionsList(questions, container.id)
              )
            })[0]?.id
          }
        }

        lastOverId.current = overId

        return [{ id: overId }]
      }

      // When a draggable item moves to a new container, the layout may shift
      // and the `overId` may become `null`. We manually set the cached `lastOverId`
      // to the id of the draggable item that was moved to the new container, otherwise
      // the previous `overId` will be returned which can cause items to incorrectly shift positions
      if (recentlyMovedToNewSection.current) {
        lastOverId.current = activeId
      }

      // If no droppable is matched, return the last match
      return lastOverId.current ? [{ id: lastOverId.current }] : []
    },
    [activeId, sectionsList]
  )

  useEffect(() => {
    // if section list is updated
    // update also fieldConfigurations
    setFieldConfigurations(prevValues => {
      const editablePrevValues = cloneDeep(prevValues)
      editablePrevValues[pageIndex].page_questions = sectionsList
      return editablePrevValues
    })
    // check items in each section
    let empty = true
    sectionsList?.forEach(section => {
      const sectionKey = getFirstKeyFromObject(section)
      const questions = section[sectionKey].questions
      if (questions?.length > 0) {
        empty = false
      }
    })
    setEmptySections(empty)
    requestAnimationFrame(() => {
      recentlyMovedToNewSection.current = false
    })

    setExtraConditions(checkElementsConditions(sectionsList))
  }, [sectionsList])

  const findContainer = id => {
    if (checkIdInSectionsList(id)) {
      return id
    }
    const foundSectionBasedOnQuestionId = sectionsList.find(section => {
      const itemKey = getFirstKeyFromObject(section)
      const itemData = section[itemKey]
      const questions = itemData.questions
      const questionFound = questions.find(
        question => getFirstKeyFromObject(question) === id
      )
      if (questionFound) return true
      else return false
    })
    if (foundSectionBasedOnQuestionId) {
      return getFirstKeyFromObject(foundSectionBasedOnQuestionId)
    }
  }

  const getSectionQuestions = (sections, sectionId) => {
    if (!sections || !sectionId) return []
    const foundSection = sections.find(
      section => sectionId === getFirstKeyFromObject(section)
    )
    const sectionKey = getFirstKeyFromObject(foundSection)
    const sectionQuestions = foundSection[sectionKey]?.questions ?? []
    return sectionQuestions
  }

  const getQuestionIndexById = (questions, questionId) => {
    return questions.findIndex(
      question => getFirstKeyFromObject(question) === questionId
    )
  }

  const checkIdInSectionsList = sectionId => {
    const foundSection = sectionsList.find(
      section => sectionId === getFirstKeyFromObject(section)
    )
    if (foundSection) return true
    else return false
  }

  const checkIdInDraggableComponents = draggableId => {
    const foundDraggable = draggableComponents.find(
      component => draggableId === getFirstKeyFromObject(component)
    )
    if (foundDraggable) return true
    else return false
  }

  const dragOverHandler = ({ active, over }) => {
    const overId = over?.id
    const isOverIdInSectionsList = sectionsList.find(
      item => getFirstKeyFromObject(item) === active.id
    )
    if (overId == null || overId === TRASH_ID || !!isOverIdInSectionsList) {
      return
    }
    const overContainer = findContainer(overId)
    const activeContainer = findContainer(active.id)
    // if draggableComponent is over components in the middle panel
    // add item to the middle panel
    if (overContainer && checkIdInDraggableComponents(activeId)) {
      // check active Id in droppable components
      const foundComponentIndex = draggableComponents.findIndex(
        element => active.id === getFirstKeyFromObject(element)
      )
      if (foundComponentIndex !== -1) {
        if (!changes) setChanges(true)
        setSectionsList(prevSectionsList => {
          const modSectionsList = cloneDeep(prevSectionsList)
          const overItems = getSectionQuestions(modSectionsList, overContainer)
          const overIndex = getQuestionIndexById(overItems, overId)
          const newIndex = overIndex >= 0 ? overIndex : overItems.length + 1
          const overSectionIdx = sectionsList.findIndex(
            section => getFirstKeyFromObject(section) === overContainer
          )
          const overSectionKey = getFirstKeyFromObject(
            modSectionsList[overSectionIdx]
          )
          const draggableCmp = draggableComponents[foundComponentIndex]
          changeTotalAmountDueToFalse(draggableCmp)
          modSectionsList[overSectionIdx][overSectionKey].questions = [
            ...modSectionsList[overSectionIdx][overSectionKey].questions.slice(
              0,
              newIndex
            ),
            draggableCmp,
            ...modSectionsList[overSectionIdx][overSectionKey].questions.slice(
              newIndex,
              modSectionsList[overSectionIdx][overSectionKey].questions.length
            )
          ]
          return modSectionsList
        })
        replaceDraggedComponent(foundComponentIndex)
      }
    }

    if (!overContainer || !activeContainer) {
      return
    }
    if (activeContainer !== overContainer) {
      if (!changes) setChanges(true)
      setSectionsList(prevSectionsList => {
        // question from active containerId section
        const activeItems = getSectionQuestions(
          prevSectionsList,
          activeContainer
        )
        const overItems = getSectionQuestions(prevSectionsList, overContainer)
        const overIndex = getQuestionIndexById(overItems, overId)
        const activeIndex = getQuestionIndexById(activeItems, active.id)
        let newIndex
        if (checkIdInSectionsList(overId)) {
          newIndex = overItems.length + 1
        } else {
          const isBelowOverItem =
            over &&
            active.rect.current.translated &&
            active.rect.current.translated.top >
              over.rect.top + over.rect.height

          const modifier = isBelowOverItem ? 1 : 0

          newIndex =
            overIndex >= 0 ? overIndex + modifier : overItems.length + 1
        }
        recentlyMovedToNewSection.current = true

        const modSectionsList = cloneDeep(prevSectionsList)
        // update active Section
        const activeSectionIdx = sectionsList.findIndex(
          item => getFirstKeyFromObject(item) === activeContainer
        )
        const activeSectionKey = getFirstKeyFromObject(
          modSectionsList[activeSectionIdx]
        )
        modSectionsList[activeSectionIdx][activeSectionKey].questions =
          modSectionsList[activeSectionIdx][activeSectionKey].questions.filter(
            question => active.id !== getFirstKeyFromObject(question)
          )
        // update over Section
        const overSectionIdx = sectionsList.findIndex(
          section => getFirstKeyFromObject(section) === overContainer
        )
        const overSectionKey = getFirstKeyFromObject(
          modSectionsList[overSectionIdx]
        )
        modSectionsList[overSectionIdx][overSectionKey].questions = [
          ...modSectionsList[overSectionIdx][overSectionKey].questions.slice(
            0,
            newIndex
          ),
          sectionsList[activeSectionIdx][activeSectionKey].questions[
            activeIndex
          ],
          ...modSectionsList[overSectionIdx][overSectionKey].questions.slice(
            newIndex,
            modSectionsList[overSectionIdx][overSectionKey].questions.length
          )
        ]
        return modSectionsList
      })
    }
  }

  const dragEndHandler = ({ active, over }) => {
    if (checkIdInSectionsList(active.id) && over?.id) {
      if (!changes) setChanges(true)
      setSectionsList(prevSections => {
        const activeIdx = prevSections.findIndex(
          item => getFirstKeyFromObject(item) === active.id
        )
        const overIdx = prevSections.findIndex(
          item => getFirstKeyFromObject(item) === over.id
        )
        return arrayMove(sectionsList, activeIdx, overIdx)
      })
    }
    const activeContainer = findContainer(active.id)
    if (!activeContainer) {
      setActiveId(null)
      return
    }

    const overId = over?.id
    if (overId == null) {
      setActiveId(null)
      return
    }
    if (overId === TRASH_ID) {
      handleRemoveElementAction(active.id)
      setActiveId(null)
      return
    }
    const overContainer = findContainer(overId)
    if (overContainer) {
      const overContainerIdx = sectionsList.findIndex(
        section => getFirstKeyFromObject(section) === overContainer
      )
      const overQuestions = getSectionQuestions(sectionsList, overContainer)
      const activeIndex = overQuestions?.findIndex(
        question => active.id === getFirstKeyFromObject(question)
      )
      const overIndex = overQuestions?.findIndex(
        question => overId === getFirstKeyFromObject(question)
      )

      if (activeIndex !== overIndex) {
        if (!changes) setChanges(true)
        setSectionsList(prevSectionsList => {
          const modSectionsList = cloneDeep(prevSectionsList)
          const overSectionKey = getFirstKeyFromObject(
            modSectionsList[overContainerIdx]
          )
          modSectionsList[overContainerIdx][overSectionKey].questions =
            arrayMove(
              modSectionsList[overContainerIdx][overSectionKey].questions,
              activeIndex,
              overIndex
            )
          return modSectionsList
        })
      }
    }
    setActiveId(null)
  }

  // main
  return (
    <Fragment>
      <Container
        disableGutters
        maxWidth
        className={classes.formBuilderTabsContainer}
      >
        <TopBar
          formName={formName}
          status={status}
          onToggleChange={toggleChangeHandler}
          onBack={backHandler}
          onFormNameChange={formNameChangeHandler}
        />
        <DndContext
          onDragCancel={dragCancelHandler}
          collisionDetection={collisionDetectionStrategy}
          sensors={sensors}
          onDragStart={({ active }) => {
            setActiveId(active.id)
            setClonedSectionsList(sectionsList)
          }}
          onDragOver={dragOverHandler}
          onDragEnd={dragEndHandler}
        >
          <Stack className={classes.panelsStack} direction="row" spacing={2}>
            <LeftPanel
              extraConditions={extraConditions}
              activeId={activeId}
              status={status}
              formDescription={formDescription}
              onFormDescriptionChange={formDescriptionHandler}
              trade={trade}
              service={service}
              woType={woType}
              tradeList={tradeList}
              serviceList={serviceList}
              typeList={typeList}
              onTradeChange={tradeChangeHandler}
              onServiceChange={serviceChangeHandler}
              onWoTypeChange={woTypeChangeHandler}
              draggableComponents={draggableComponents}
              onClickAdd={clickAddHandler}
              configurationsField={fieldConfigurations}
              pageIndex={pageIndex}
              onAddSection={addSectionHandler}
              onEditSectionName={editSectionNameHandler}
              onRemoveSection={removeSectionHandler}
            />
            <MiddlePanel
              recentlyAddedId={recentlyAddedItemId}
              activeId={activeId}
              emptySections={emptySections}
              pageIndex={pageIndex}
              onEditSectionName={editSectionHandlerMiddlePanel}
              onShowPreview={showPreviewHandler}
              selectedObjectCounter={selectedObjectCounter}
              mandatoryFields={mandatoryFields}
              activeItem={selectedElement}
              status={status}
              changes={changes}
              setChanges={setChanges}
              missingData={missingData}
              onSaveButtonClick={saveButtonHandler}
              onDeactivateButtonClick={deactivateButtonHandler}
              onPublishButtonClick={publishButtonHandler}
              setSelectedItem={setSelectedElement}
              onSelectItem={selectItemHandler}
              configurationsField={fieldConfigurations}
              sectionsList={sectionsList}
              bottomRef={bottomRef}
            />
            <RightPanel
              extraConditions={extraConditions}
              emptySections={emptySections}
              status={status}
              onNestedLogicTrue={nestedLogicTrueHandler}
              onNestedLogicFalse={nestedLogicFalseHandler}
              onUpdateNestedLogic={updateNestedLogicItemHandler}
              selectedObjectCounter={selectedObjectCounter}
              mandatoryFields={mandatoryFields}
              selectedItem={selectedElement}
              setSelectedItem={setSelectedElement}
              changes={changes}
              setChanges={setChanges}
              onDeleteElement={handleRemoveElementAction}
              onCloneElement={handleCloneElement}
            />
          </Stack>
        </DndContext>
      </Container>
      <ConfirmationModal
        customStyles={customTheme.modalDeactivateStyle}
        title={title}
        message={message}
        open={showConfirmationModal}
        onMainAction={mainActionModalHandler}
        onSecondaryAction={secondaryActionModalHandler}
        mainActionLabel={mainActionLabel}
        secondaryActionLabel={secondaryActionLabel}
        hideSecondaryAction={hideSecondaryAction}
        extraMessage={extraMessage}
        mainButtonStyle={mainButtonStyle}
        extraMessageStyle={extraMessageStyle}
      />
      <Modal
        open={showPreview}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <div>
          <FormPreview
            formName={formName}
            onShowPreview={showPreviewHandler}
            configurations={fieldConfigurations}
          />
        </div>
      </Modal>
    </Fragment>
  )
}

export default FormBuilder
